/**
 * @file Audio and video record mixin
 * */
import { trackEvent } from 'bits/analytics'
import fetchElectronBridge from 'bits/electron_bridge'
import { secondsToSemiColonedText } from 'bits/time'
import ysFixWebmDuration from 'fix-webm-duration'
import { supportedAudioCodec, supportedVideoCodec } from '../bits/av_recorder'

// Caching by name because Safari generates a different ID every time.
const ACTIVE_AUDIO_DEVICE_NAME_CACHE_KEY = 'activeAudioDeviceName'
const ACTIVE_VIDEO_DEVICE_NAME_CACHE_KEY = 'activeVideoDeviceName'

/**
 * Recording statuses
 * 1. notstarted: no form of recording has been initiated
 * 2. recording: recording is on-going
 * 3. paused: recording is paused
 * 4. restarting: recording is in the middle of a restart
 * 5. stopping: recording has stopped, but data is not available
 * 6. stopped: recording has stopped and data is available for use
 */

const AVRecorderMixin = {
  data() {
    return {
      activeAudioDeviceId: null,
      activeVideoDeviceId: null,
      audioDevices: [],
      // durationMs is the duration of the recorded video in milliseconds.
      // It was added to fix the duration of webm videos. Don't confuse it
      // with secondsElapsed which is in seconds and is used to tell whether
      // the recording has reached its time limit or not.
      // TODO: remove secondsElapsed and use durationMs to check recording
      // limit.
      durationMs: 0,
      maxDuration: 60,
      bufferDuration: 0,
      recordedChunks: [],
      recording: false,
      recordingOptions: {},
      recordingStatus: 'notstarted',
      recordingStartTimeMs: null,
      secondsElapsed: 0,
      streaming: false,
      videoDevices: [],
    }
  },
  computed: {
    constraints() {
      // implement your own
    },
    hasChunks() {
      return this.recordingStatus != 'notstarted'
    },
    hasRecordedChunks() {
      return this.recordedChunks.length > 0 && this.recordingStatus != 'notstarted'
    },
    hasReachedRecordingLimit() {
      return this.secondsElapsed >= this.maxDuration + this.bufferDuration
    },
    isReachingRecordingLimit() {
      // implement your own
    },
    recordingButtonTitle() {
      return this.hasChunks ? this.__('Resume recording') : this.__('Start recording')
    },
    recordingDurationText() {
      return secondsToSemiColonedText(this.secondsElapsed)
    },
  },
  watch: {
    hasReachedRecordingLimit(value) {
      if (value) {
        this.pauseRecording()
      }
    },
  },
  methods: {
    chooseAudioDevice(e) {
      this.activeAudioDeviceId = e.target.value
      if (e.target.value !== 'no_audio') {
        const label = this.audioDevices.find((x) => x.id === this.activeAudioDeviceId).label
        localStorage.setItem(ACTIVE_AUDIO_DEVICE_NAME_CACHE_KEY, label)
      }
      if (this.streaming) {
        this.$nextTick(this.restartStreaming.bind(this))
      }
    },
    chooseVideoDevice(e) {
      this.activeVideoDeviceId = e.target.value
      const label = this.videoDevices.find((x) => x.id === this.activeVideoDeviceId).label
      localStorage.setItem(ACTIVE_VIDEO_DEVICE_NAME_CACHE_KEY, label)
      this.$nextTick(this.restartStreaming.bind(this))
    },
    _gotRecordedData(e) {
      this.recordingStatus = 'stopped'
      this.recordedChunks = this.recordedChunks.concat([e.data])
      this.gotRecordedData()
    },
    gotRecordedData() {
      // implement your own
    },
    gotDevices(deviceInfos) {
      /**
       * We come to this method after the stream has started once
       * because that's how Safari handles it.
       * (Ideally we'd like to come here before even starting a stream)
       *
       * Since we cache the user's preferred device, there are 4 scenarios
       *
       * 1. The user has no preferred device cached
       * 2. The user has a preferred device and that is the one streaming
       * 3. The user has a preferred device but it's not connected anymore
       * 4. The user has a connected preferred device and a different one is streaming
       *
       * If 1, 2, or 3, do nothing. If 4, switch to the preferred device and restart streaming.
       * This happens for both video and audio.
       */

      // First, we create a list of available audio and video devices.
      const videoDevices = []
      const audioDevices = []
      if (deviceInfos) {
        for (let { deviceId, label, kind } of deviceInfos) {
          if (kind === 'videoinput') {
            videoDevices.push({ id: deviceId, label: label })
          } else if (kind == 'audioinput') {
            audioDevices.push({ id: deviceId, label: label })
          }
        }
      }

      this.videoDevices = videoDevices
      this.audioDevices = audioDevices

      // Read cache and determine if streaming should be restarted
      let shouldRestartStreaming = false

      const cachedActiveVideoDeviceLabel = localStorage.getItem(ACTIVE_VIDEO_DEVICE_NAME_CACHE_KEY) || null
      const cachedActiveAudioDeviceLabel = localStorage.getItem(ACTIVE_AUDIO_DEVICE_NAME_CACHE_KEY) || null

      // Audio
      if (this.constraints.audio && cachedActiveAudioDeviceLabel) {
        const cachedDevice = audioDevices.find((x) => x.label === cachedActiveAudioDeviceLabel)
        if (cachedDevice) {
          if (cachedDevice.id === this.activeAudioDeviceId) {
            // Scenario 2, do nothing
          } else {
            // Scenario 4, restart streaming
            this.activeAudioDeviceId = cachedDevice.id
            shouldRestartStreaming = true
          }
        } else {
          // Scenario 3, do nothing
        }
      } else {
        // Scenario 1, do nothing
      }

      // Video
      if (this.constraints.video && cachedActiveVideoDeviceLabel) {
        const cachedDevice = videoDevices.find((x) => x.label === cachedActiveVideoDeviceLabel)
        if (cachedDevice) {
          if (cachedDevice.id === this.activeVideoDeviceId) {
            // Scenario 2, do nothing
          } else {
            // Scenario 4, restart streaming
            this.activeVideoDeviceId = cachedDevice.id
            shouldRestartStreaming = true
          }
        } else {
          // Scenario 3, do nothing
        }
      } else {
        // Scenario 1, do nothing
      }

      if (shouldRestartStreaming) {
        this.restartStreaming()
      }
    },
    _gotStream(stream) {
      this.streaming = true
      this._stream = stream
      if (this.constraints.video) {
        const videoTrack = stream.getVideoTracks()[0]
        videoTrack.addEventListener('ended', () => {
          // Event when user press stop recording for screensharing
          // or suddenly block webcam during recording
          this.stopRecording()
        })
        if (videoTrack && typeof videoTrack.getSettings == 'function') {
          const settings = videoTrack.getSettings()
          if (settings.deviceId) {
            this.activeVideoDeviceId = settings.deviceId
          }
        }
      }
      if (this.constraints.audio) {
        const audioTrack = stream.getAudioTracks()[0]
        if (audioTrack && typeof audioTrack.getSettings == 'function') {
          const settings = audioTrack.getSettings()
          if (settings.deviceId) {
            this.activeAudioDeviceId = settings.deviceId
          }
        }
      }
      this.gotStream(stream)
    },
    gotStream(stream) {
      // implement your own
    },
    errorGettingStream(error) {
      // implement your own (optional)
      console.error(error)
    },
    mountRecording(recordingType) {
      if (recordingType == 'audio') {
        this.recordingOptions = { mimeType: supportedAudioCodec }
      } else {
        this.recordingOptions = { mimeType: supportedVideoCodec }
      }
    },
    mountScreenStreaming() {
      if (this.isElectronApp) {
        const constraintsVideo = {
          audio: false,
          video: this.constraints.video,
        }

        navigator.mediaDevices.getUserMedia(constraintsVideo).then((stream) => {
          // Record screen without audio
          if (!this.constraints.audio) {
            this._gotStream(stream)
            return
          }

          const electronApp = fetchElectronBridge()
          // Request microphone to record screen with audio
          if (electronApp && !this.audioDevices.length) {
            electronApp.requestMic()
          }

          // Record screen with audio
          navigator.mediaDevices
            .getUserMedia({
              audio: this.constraints.audio,
            })
            .then((audioStream) => {
              stream.addTrack(audioStream.getAudioTracks()[0])
              this._gotStream(stream)
            })
        })
      } else if (this.constraints.audio === 'chrome') {
        // We don't need to get devices for screensharing
        let constraints = {
          audio: false,
          video: this.constraints.video,
        }

        navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
          navigator.mediaDevices
            .getUserMedia({
              audio: true,
            })
            .then((audioStream) => {
              stream.addTrack(audioStream.getAudioTracks()[0])
              this._gotStream(stream)
            })
        })
      } else {
        navigator.mediaDevices
          .getUserMedia(this.constraints)
          .then((stream) => {
            this._gotStream(stream)
          })
          .catch((error) => {
            this.errorGettingStream(error)
          })
      }
    },
    mountStreaming() {
      // Because of Fingerprinting issues, Safari doesn't let you get
      // a list of devices until you start streaming first.
      // So we start streaming, get devices, and restart streaming
      // with the right default camera.
      navigator.mediaDevices
        .getUserMedia(this.constraints)
        .then((stream) => {
          this._gotStream(stream)
          return navigator.mediaDevices.enumerateDevices()
        })
        .then(this.gotDevices.bind(this))
    },
    mountToSetupAudioStreaming() {
      return navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          const audioTrack = stream.getAudioTracks()[0]
          if (typeof audioTrack.getSettings == 'function') {
            const settings = audioTrack.getSettings()
            if (settings.deviceId) {
              this.activeAudioDeviceId = settings.deviceId
            }
          }
          return navigator.mediaDevices.enumerateDevices()
        })
        .catch((error) => {
          if (error.name === 'NotAllowedError') {
            console.warn('Microphone permission not allowed')
          } else {
            throw error
          }
        })
        .then(this.gotDevices.bind(this))
    },
    /**
     * Pause the recording.
     * Only changes the status. No data changes are made anywhere.
     */
    pauseRecording() {
      if (!this.recordingStatus == 'recording') return

      this._mediaRecorder.pause()
      this.recordingStatus = 'paused'
      this.recording = false
      clearInterval(this._recordingTimer)
      this.durationMs += Date.now() - this.recordingStartTimeMs
    },
    async recordedBlob() {
      let blob = new Blob(this.recordedChunks, { type: this.recordingOptions.mimeType })
      if (this.recordingOptions.mimeType.startsWith('video/webm')) {
        blob = await ysFixWebmDuration(blob, this.durationMs)
      }
      return blob
    },
    restartStreaming() {
      trackEvent('AV Recorder', 'Restarted')
      this.stopStreaming()
      this.startStreaming()
    },
    resumeRecording() {
      trackEvent('AV Recorder', 'Resumed')
      this._mediaRecorder.resume()
      this.recordingStatus = 'recording'
      this.recording = true
      this._recordingTimer = setInterval(() => {
        this.secondsElapsed++
      }, 1000)
      this.recordingStartTimeMs = Date.now()
    },
    /**
     * Restart the recording.
     * This is done by discarding the current recording and starting a new one.
     * When you discard a current feed in mediarecorder
     */
    discardRecording() {
      trackEvent('AV Recorder', 'Restarted')
      this.secondsElapsed = 0
      this.recordedChunks = []
      // When we stop media recorder, it makes one data available call.
      // Since we are not interested in it, we'll simply ignore it.
      if (this._boundDataAvailableCallback) {
        this._mediaRecorder.removeEventListener('dataavailable', this._boundDataAvailableCallback)
      }
      this.stopRecording()
      this.durationMs = 0
    },
    restartRecording() {
      this.discardRecording()
      this.startRecording()
    },
    startRecording() {
      // if recording limit has been reached, don't start
      if (this.hasReachedRecordingLimit) return

      // if already recording, ignore
      if (this.recordingStatus == 'recording') return

      // if paused, resume
      if (this.recordingStatus == 'paused') return this.resumeRecording()

      // otherwise start
      if (this.recordingStatus == 'notstarted') {
        trackEvent('AV Recorder', 'Started')
      }
      this._mediaRecorder = new MediaRecorder(this._stream, this.recordingOptions)
      if (!this._boundDataAvailableCallback) {
        this._boundDataAvailableCallback = this._gotRecordedData.bind(this)
      }

      this._mediaRecorder.addEventListener('dataavailable', this._boundDataAvailableCallback)

      this._mediaRecorder.start()
      this.recording = true
      this.recordingStatus = 'recording'

      this._recordingTimer = setInterval(() => {
        this.secondsElapsed++
      }, 1000)
      this.recordingStartTimeMs = Date.now()
    },
    startStreaming() {
      return navigator.mediaDevices.getUserMedia(this.constraints).then(this._gotStream.bind(this))
    },
    stopRecording() {
      if (this._mediaRecorder && this.recordingStatus != 'stopped' && this.recordingStatus != 'stopping') {
        this.recordingStatus = 'stopping'
        this.recording = false
        clearInterval(this._recordingTimer)
        // When this mixin is used by screenshare_booth, pauseRecording is not called (we cannot pause a
        // screenshare). So we calculate the duration when the screenshare is stopped.
        if (this.durationMs === 0) {
          this.durationMs = Date.now() - this.recordingStartTimeMs
        }
        this.recordingStartTimeMs = null
        this._mediaRecorder.stop()
      }
    },
    stopStreaming() {
      // it can be false if permission was never granted.
      this.streaming = false
      if (this._stream) {
        this._stream.getTracks().forEach((track) => track.stop())
      }
    },
  },
}

export default AVRecorderMixin
